'use client';

import { isEmpty, uniq, uniqBy } from 'lodash';

import { MiniMap } from '@/components/modules/mini-map';
import { Text } from '@/components/text';
import { usePaths } from '@/hooks/use-paths';
import { useVesselLocations } from '@/hooks/use-vessel-locations';
import { MAP_LINE_COLOR_LIVE, MAP_LINE_STYLE_DASHED_LIGHT, MAP_LINE_STYLE_SOLID } from '@/lib/constants';
import { ShipmentMilestone, ShipmentType, ShipmentVehicleModel } from '@/lib/models/shipment/types';
import { ShipmentJourneyModel } from '@/lib/models/shipment-journey/types';
import { getShipmentJourneyVessels } from '@/lib/models/shipment-journey/utils/get-shipment-journey-vessels';
import { getUniquePortsFromShipmentJourney } from '@/lib/models/shipment-journey/utils/get-unique-ports';
import { MapMarker } from '@/types';
import { convertShipmentModeToSegmentMode } from '@/utils/format/mode';
import { createMarkersFromPorts, pathsToMapPaths } from '@/utils/mapping';
import { tv } from '@/utils/styles';

const getCompletedPortsFromShipmentMilestones = (milestones?: ShipmentMilestone[]): string[] => {
  if (!milestones || isEmpty(milestones)) return [];
  const completedEvents = milestones.filter((milestone) => milestone.completed);
  const codes = completedEvents.map((port) => port.locationCode);
  return uniq(codes);
};

export interface ShipmentMapProps {
  vehicles: ShipmentVehicleModel[]; // @deprecated, @TODO remove
  markers: MapMarker[];
  type: ShipmentType;
  journey?: ShipmentJourneyModel;
  milestones?: ShipmentMilestone[];
}

const ShipmentMap = ({ journey, milestones, markers = [], vehicles, type }: ShipmentMapProps) => {
  const mode = convertShipmentModeToSegmentMode(type);
  const portsArr = journey ? getUniquePortsFromShipmentJourney(journey) : [];
  const completedPortIds = getCompletedPortsFromShipmentMilestones(milestones);
  const oldPortMapMarkers = markers.filter((m) => m.nodeType !== 'vehicle'); // @TODO deprecated, remove in JAN 2025
  const portMapMarkers = !isEmpty(portsArr) ? createMarkersFromPorts(portsArr, completedPortIds) : oldPortMapMarkers;
  const vessels = getShipmentJourneyVessels(journey) || vehicles;

  // Get predicted paths
  // -------------------
  const { paths: predictedPaths } = usePaths(portMapMarkers, mode, true);

  const predictedMapPaths = pathsToMapPaths(predictedPaths, {
    pathStyle: MAP_LINE_STYLE_DASHED_LIGHT,
    hasOutline: false,
  });

  // get live vessel locations
  // -------------------
  const { markers: vesselMarkers, paths: vehiclePaths } = useVesselLocations({ vehicles: vessels });

  const vehicleMapPaths = pathsToMapPaths(vehiclePaths, {
    pathColor: MAP_LINE_COLOR_LIVE,
    pathStyle: MAP_LINE_STYLE_SOLID,
    hasOutline: false,
    strokeWeight: 3,
  });

  const combinedMarkers = isEmpty(vesselMarkers)
    ? uniqBy([...markers, ...portMapMarkers], 'id')
    : uniqBy([...vesselMarkers, ...portMapMarkers], 'id');
  const combinedPaths = [...predictedMapPaths, ...vehicleMapPaths];

  const hasMarkers = !isEmpty(combinedMarkers);

  const { base, legend, row, predictedLine, actualLine, container, map, mapEmptyMessage } = styles();

  return (
    <article>
      <div className={map()}>
        <div className={base({ className: 'chromatic-ignore' })}>
          <div className={container()}>
            {hasMarkers && (
              <>
                <div className={legend()}>
                  <div className={row()}>
                    <span className={predictedLine()} />
                    <Text variant="label" className="m-0">
                      Predicted path
                    </Text>
                  </div>
                  <div className={row()}>
                    <span className={actualLine()} />
                    <Text variant="label" className="m-0">
                      Actual path
                    </Text>
                  </div>
                </div>
                <MiniMap markers={combinedMarkers} paths={combinedPaths} options={{ maxZoom: 5 }} />
              </>
            )}
            {!hasMarkers && <div className={mapEmptyMessage()}>No location data available</div>}
          </div>
        </div>
      </div>
    </article>
  );
};

const styles = tv({
  slots: {
    base: 'relative h-full w-full',
    container:
      'absolute bottom-0 left-0 right-0 top-0 grid place-items-center bg-lightBlue-50 bg-[url("~public/assets/map-background-light.png")] bg-cover',
    legend:
      'absolute bottom-6 right-4 z-2 flex flex-row gap-4 rounded-md bg-white p-3 text-baseSm shadow-lg max-md:hidden',
    row: 'flex items-center gap-2',
    predictedLine: 'w-4 border-t-2 border-dashed border-grey-400',
    actualLine: 'w-4 border-t-2 border-lightBlue-400',
    map: 'h-[450px] overflow-hidden rounded-md transition-all duration-200 md:sticky md:max-h-[80vh]',
    mapEmptyMessage: 'flex h-8 items-center rounded-xl bg-white px-4 text-baseSm text-grey-600',
  },
});

export { ShipmentMap };
